import React, { useState, useCallback } from 'react';
import { Button, Space, Tooltip, Modal, message } from 'antd';
import {
  CheckOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import TransactionService from '../../service/transaction-service/Transaction.service';
import dayjs from 'dayjs';
import useEmployee from '../../hook/useEmployee';

interface WithdrawalTableActionsProps {
  record: any;
  loading: boolean;
  onRefresh: () => void;
  formatCurrency: (amount: number) => string;
  formatCardNumber: (cardNumber?: string) => string;
  onOptimisticUpdate?: (transactionId: number, newStatus: string) => void; // Thêm prop mới
}

const WithdrawalTableActions: React.FC<WithdrawalTableActionsProps> = ({
  record,
  loading,
  onRefresh,
  formatCurrency,
  formatCardNumber,
  onOptimisticUpdate,
}) => {
  const { employee } = useEmployee();
  // State riêng cho từng button để tránh double click
  const [checkLoading, setCheckLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [processingTransactionId, setProcessingTransactionId] = useState<
    number | null
  >(null);

  // Debounce mechanism để tránh request quá nhanh
  const debounce = useCallback((func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  }, []);

  // Handle check transaction (toggle status) - No confirmation needed
  const handleCheckTransaction = useCallback(
    debounce(async () => {
      // Ngăn chặn double click và đảm bảo chỉ xử lý 1 transaction tại 1 thời điểm
      if (checkLoading || processingTransactionId === record.id) {
        console.log('Transaction đang được xử lý, bỏ qua request');
        return;
      }

      try {
        setCheckLoading(true);
        setProcessingTransactionId(record.id);

        console.log(
          `Bắt đầu toggle status cho transaction ${record.id} từ ${record.status}`
        );

        // Optimistic update - cập nhật UI ngay lập tức
        const expectedNewStatus =
          record.status === 'PENDING' ? 'COMPLETED' : 'PENDING';
        if (onOptimisticUpdate) {
          onOptimisticUpdate(record.id, expectedNewStatus);
        }

        // Gọi API và nhận response với data đã cập nhật
        const response = await TransactionService.toggleWithdrawalStatus(
          record.id
        );

        console.log(`Toggle status thành công cho transaction ${record.id}`);
        console.log('Response data:', response);

        // Cập nhật message dựa trên response thực tế
        const newStatus = response.data?.status;
        const isCompleted = newStatus === 'COMPLETED';

        message.success(
          `Đã ${isCompleted ? 'xác nhận' : 'hủy xác nhận'} giao dịch thành công`
        );

        // Refresh data sau khi thành công để đảm bảo sync
        onRefresh();
      } catch (error: any) {
        console.error('Error toggling transaction status:', error);

        // Revert optimistic update nếu có lỗi
        if (onOptimisticUpdate) {
          onOptimisticUpdate(record.id, record.status);
        }

        message.error(
          error.message || 'Không thể cập nhật trạng thái giao dịch'
        );
      } finally {
        setCheckLoading(false);
        setProcessingTransactionId(null);
      }
    }, 500), // Tăng debounce lên 500ms
    [
      checkLoading,
      record.id,
      record.status,
      onRefresh,
      processingTransactionId,
      onOptimisticUpdate,
    ]
  );

  // Handle delete transaction
  const handleDeleteTransaction = useCallback(() => {
    // Ngăn chặn double click
    if (deleteLoading) return;

    Modal.confirm({
      title: 'Xác nhận xóa giao dịch',
      content: (
        <div>
          <p>
            Bạn có chắc chắn muốn <strong>xóa</strong> giao dịch này?
          </p>
          <div className="text-gray-600 text-sm mt-2">
            <p>• Thẻ: {formatCardNumber(record.creditCard?.cardNumber)}</p>
            <p>• Số tiền: {formatCurrency(record.amount)}</p>
            <p>
              • Ngày giao dịch:{' '}
              {dayjs(record.transactionDate).format('DD-MM-YYYY')}
            </p>
          </div>
          <p className="text-red-600 font-medium mt-2">
            ⚠️ Hành động này không thể hoàn tác!
          </p>
        </div>
      ),
      okText: 'Xóa',
      cancelText: 'Hủy',
      okType: 'danger',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      onOk: async () => {
        try {
          setDeleteLoading(true);
          await TransactionService.softDeleteTransaction(record.id, employee?.id as number);
          message.success('Xóa giao dịch thành công');
          onRefresh();
        } catch (error: any) {
          console.error('Error deleting transaction:', error);
          message.error(error.message || 'Không thể xóa giao dịch');
        } finally {
          setDeleteLoading(false);
        }
      },
    });
  }, [
    deleteLoading,
    record.id,
    record.creditCard?.cardNumber,
    record.amount,
    record.transactionDate,
    formatCardNumber,
    formatCurrency,
    onRefresh,
  ]);

  return (
    <Space size="small">
      <Tooltip
        title={
          record.status === 'COMPLETED'
            ? 'Hủy xác nhận nhận tiền'
            : 'Xác nhận đã nhận tiền từ POS'
        }
      >
        <Button
          type={record.status === 'COMPLETED' ? 'primary' : 'default'}
          size="small"
          icon={<CheckOutlined />}
          onClick={handleCheckTransaction}
          loading={checkLoading && processingTransactionId === record.id}
          disabled={
            checkLoading ||
            deleteLoading ||
            loading ||
            processingTransactionId === record.id
          }
          style={{
            backgroundColor:
              record.status === 'COMPLETED' ? '#52c41a' : '#f0f0f0',
            borderColor: record.status === 'COMPLETED' ? '#52c41a' : '#d9d9d9',
            color: record.status === 'COMPLETED' ? '#fff' : '#595959',
          }}
        />
      </Tooltip>

      <Tooltip title="Xóa giao dịch">
        <Button
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={handleDeleteTransaction}
          loading={deleteLoading || loading}
          disabled={
            checkLoading ||
            deleteLoading ||
            loading ||
            processingTransactionId === record.id
          }
        />
      </Tooltip>
    </Space>
  );
};

export default WithdrawalTableActions;
